import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MapPin, Star, Clock, Heart, MessageCircle, Share2, Bookmark,
  BadgeCheck, Crown, Sparkles, Plus, TrendingUp, Users,
  Tag, X, Check, Flame, ShoppingBag, ChefHat, ArrowRight,
  Navigation as NavIcon, Zap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation as useLocationCtx } from '../contexts/LocationContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'for_you',     label: 'For You',        icon: Sparkles },
  { key: 'following',    label: 'Following',      icon: Users },
  { key: 'trending',     label: 'Trending',       icon: TrendingUp },
  { key: 'deals',        label: 'Deals & Offers', icon: Tag },
  { key: 'home_cooked',  label: 'Home-Cooked',    icon: ChefHat },
];

const VIBE_CHIPS = ['Energizing', 'Warm & Cozy', 'Indulgent', 'Fresh & Light', 'Quick Bite', 'Surprise Me'];

function timeAgo(dateStr) {
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

/* ═══════════════════════════════════════════════════════════════════════════
   FEED CARD — Uber Eats–inspired premium card
   ═══════════════════════════════════════════════════════════════════════════ */

const FeedCard = ({ item, onLike, onSave }) => {
  const navigate = useNavigate();
  const img = item.image_url || item.image || item.images?.[0]?.url
    || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=75';
  const isLiked = item._isLiked || false;
  const isSaved = item._isSaved || false;
  const isDeal = item.feed_type === 'deal';
  const isPromoted = item.is_promoted || item.is_featured;

  return (
    <article
      className="group bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300"
      data-testid={`feed-card-${item.post_id || item.content_id || ''}`}
    >
      {/* Image — large, edge-to-edge inside card */}
      <div
        className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden cursor-pointer"
        onClick={() => item.post_id && navigate(`/post/${item.post_id}`)}
      >
        <img
          src={img}
          alt={item.caption || item.title || 'Food'}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
          loading="lazy"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=75'; }}
        />

        {/* Top-left badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {isPromoted && (
            <span className="bg-[#F4C430] text-gray-900 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm">
              Promoted
            </span>
          )}
          {item.is_home_cooked && (
            <span className="bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1">
              <ChefHat className="w-3 h-3" /> Home-Cooked
            </span>
          )}
        </div>

        {/* Top-right deal badge */}
        {isDeal && item.discount_percent && (
          <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
            {item.discount_percent}% OFF
          </span>
        )}

        {/* Bottom gradient overlay */}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent h-24 pointer-events-none" />

        {/* Bottom info pills */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
          {item.vibe && (
            <span className="bg-white/20 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full">
              {item.vibe}
            </span>
          )}
          {item.cuisine && (
            <span className="bg-white/20 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full">
              {item.cuisine}
            </span>
          )}
          {item.venue_name && (
            <span className="bg-white/20 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ml-auto">
              <MapPin className="w-3 h-3" />{item.venue_name}
            </span>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="p-4 md:p-5">
        {/* Author row */}
        <div className="flex items-center gap-3 mb-3">
          <img
            src={item.user_avatar || `https://i.pravatar.cc/150?u=${item.user_id}`}
            alt=""
            className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-[15px] text-gray-900 truncate">
                {item.user_name || item.business_name || 'Foodie'}
              </span>
              {item.is_verified_business && (
                <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
              )}
              {item.is_premium_member && (
                <Crown className="w-4 h-4 text-[#F4C430] flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              {item.city && <span>{item.city}</span>}
              {item.created_at && <><span>·</span><span>{timeAgo(item.created_at)}</span></>}
            </div>
          </div>
        </div>

        {/* Caption */}
        {(item.caption || item.title || item.description) && (
          <p className="text-[15px] text-gray-800 leading-relaxed mb-3 line-clamp-3">
            {item.caption || item.title}
            {item.description && !item.caption && (
              <span className="text-gray-500"> — {item.description}</span>
            )}
          </p>
        )}

        {/* Deal pricing strip */}
        {isDeal && item.discount_price != null && (
          <div className="flex items-center gap-3 mb-3 bg-red-50 border border-red-100 px-4 py-2.5 rounded-2xl">
            <ShoppingBag className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xl font-black text-red-600">${item.discount_price}</span>
              {item.original_price && (
                <span className="text-sm text-gray-400 line-through">${item.original_price}</span>
              )}
            </div>
            {item.valid_until && (
              <span className="text-[11px] text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Until {new Date(item.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        )}

        {/* Tags */}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {item.tags.slice(0, 5).map(tag => (
              <span key={tag} className="text-xs text-[#FF6B35] font-medium hover:underline cursor-pointer">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Engagement bar */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100/80">
          {[
            { icon: Heart, count: item.likes?.length || 0, active: isLiked, color: 'text-red-500', fill: isLiked, action: () => onLike?.(item), testId: 'like-btn' },
            { icon: MessageCircle, count: item.comments_count || 0, color: 'text-blue-500', testId: 'comment-btn' },
            { icon: Share2, count: item.shares || 0, color: 'text-emerald-500', testId: 'share-btn' },
          ].map(({ icon: Icon, count, active, color, fill, action, testId }) => (
            <button
              key={testId}
              onClick={action}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-gray-400 hover:bg-gray-50 hover:${color} transition-colors`}
              data-testid={testId}
            >
              <Icon className={`w-[18px] h-[18px] ${active ? `${color} fill-current` : ''}`} />
              <span className="text-xs font-medium">{count}</span>
            </button>
          ))}
          <button
            onClick={() => onSave?.(item)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-gray-400 hover:bg-gray-50 hover:text-[#FF6B35] transition-colors"
            data-testid="save-btn"
          >
            <Bookmark className={`w-[18px] h-[18px] ${isSaved ? 'fill-[#FF6B35] text-[#FF6B35]' : ''}`} />
          </button>
        </div>
      </div>
    </article>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON LOADER — shimmer cards
   ═══════════════════════════════════════════════════════════════════════════ */

const FeedSkeleton = () => (
  <div className="space-y-5">
    {[1, 2, 3].map(i => (
      <div key={i} className="bg-white rounded-[20px] overflow-hidden animate-pulse">
        <div className="aspect-[4/3] md:aspect-[16/10] bg-gray-100" />
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100" />
            <div className="flex-1 space-y-1.5">
              <div className="h-4 bg-gray-100 rounded-full w-1/3" />
              <div className="h-3 bg-gray-100 rounded-full w-1/4" />
            </div>
          </div>
          <div className="h-3 bg-gray-100 rounded-full w-full" />
          <div className="h-3 bg-gray-100 rounded-full w-2/3" />
        </div>
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   SUBSCRIPTION MODAL — clean tier picker
   ═══════════════════════════════════════════════════════════════════════════ */

const SubscriptionModal = ({ onClose, tiers, onSubscribe }) => (
  <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <div
      className="relative bg-white w-full max-w-md rounded-t-[28px] md:rounded-[28px] shadow-2xl p-6 md:p-8 animate-in slide-in-from-bottom duration-300"
      data-testid="subscription-modal"
    >
      <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">
        <X className="w-5 h-5 text-gray-400" />
      </button>

      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-200">
          <Crown className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Go Premium</h2>
        <p className="text-sm text-gray-500 mt-2 max-w-xs mx-auto">Post & promote your food to thousands of foodies nearby</p>
      </div>

      <div className="space-y-3">
        {Object.entries(tiers || {}).map(([key, tier]) => (
          <button
            key={key}
            onClick={() => onSubscribe(key)}
            className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-200 hover:shadow-md active:scale-[0.99] ${
              key === 'pro'
                ? 'border-[#FF6B35] bg-gradient-to-br from-[#FFF5F0] to-white'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`tier-${key}-btn`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-bold text-lg text-gray-900">{tier.label}</span>
                {key === 'pro' && (
                  <span className="text-[10px] font-bold text-white bg-[#FF6B35] px-2 py-0.5 rounded-full">POPULAR</span>
                )}
              </div>
              <div className="text-right">
                <span className="text-2xl font-black text-gray-900">${tier.price}</span>
                <span className="text-xs text-gray-400">/mo</span>
              </div>
            </div>
            <ul className="space-y-2">
              {tier.features?.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
                  <div className="w-5 h-5 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-500" />
                  </div>
                  {f}
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>

      <p className="text-center text-[11px] text-gray-400 mt-6">
        Business & personal (home cooks) welcome · Cancel anytime · Powered by Stripe
      </p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE — Uber Eats–inspired Promote hub
   ═══════════════════════════════════════════════════════════════════════════ */

export default function Venues() {
  const { user } = useAuth();
  const { location: loc } = useLocationCtx();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const feedEndRef = useRef(null);

  const [activeTab, setActiveTab] = useState('for_you');
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [trendingTags, setTrendingTags] = useState([]);
  const [topMembers, setTopMembers] = useState([]);
  const [subStatus, setSubStatus] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);

  const city = loc?.city || loc?.displayName || '';

  /* ── Fetch feed ── */
  const fetchFeed = useCallback(async (tabKey, pageNum = 1, append = false) => {
    if (!append) setLoading(true);
    try {
      const params = new URLSearchParams({ tab: tabKey, page: pageNum, limit: 15 });
      if (city) params.set('city', city);
      const res = await fetch(`${API_URL}/api/venues/feed?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const enriched = data.items.map(item => ({
          ...item,
          _isLiked: user ? item.likes?.includes(user.user_id) : false,
          _isSaved: user ? item.saves?.includes(user.user_id) : false,
        }));
        setFeedItems(prev => append ? [...prev, ...enriched] : enriched);
        setHasMore(data.has_more);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [city, user]);

  useEffect(() => { setPage(1); fetchFeed(activeTab, 1); }, [activeTab, fetchFeed]);

  /* ── Sidebar data ── */
  useEffect(() => {
    fetch(`${API_URL}/api/venues/trending-tags`).then(r => r.json()).then(d => setTrendingTags(d.tags || [])).catch(() => {});
    fetch(`${API_URL}/api/venues/top-members`).then(r => r.json()).then(d => setTopMembers(d.members || [])).catch(() => {});
  }, []);

  /* ── Subscription ── */
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/venues/subscription/status`, { credentials: 'include' })
      .then(r => r.json()).then(setSubStatus).catch(() => {});
  }, [user]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (searchParams.get('subscription') === 'success' && sessionId) {
      fetch(`${API_URL}/api/venues/subscription/verify/${sessionId}`, { credentials: 'include' })
        .then(r => r.json())
        .then(() => fetch(`${API_URL}/api/venues/subscription/status`, { credentials: 'include' }).then(r => r.json()).then(setSubStatus))
        .catch(() => {});
    }
  }, [searchParams]);

  /* ── Actions ── */
  const loadMore = () => { const n = page + 1; setPage(n); fetchFeed(activeTab, n, true); };

  const handleLike = async (item) => {
    if (!user || !item.post_id) return;
    try {
      await fetch(`${API_URL}/api/posts/${item.post_id}/like`, { method: 'POST', credentials: 'include' });
      setFeedItems(prev => prev.map(fi =>
        fi.post_id === item.post_id
          ? { ...fi, _isLiked: !fi._isLiked, likes: fi._isLiked ? fi.likes.filter(id => id !== user.user_id) : [...(fi.likes || []), user.user_id] }
          : fi
      ));
    } catch { /* ignore */ }
  };

  const handleSave = async (item) => {
    if (!user || !item.post_id) return;
    try {
      await fetch(`${API_URL}/api/posts/${item.post_id}/save`, { method: 'POST', credentials: 'include' });
      setFeedItems(prev => prev.map(fi =>
        fi.post_id === item.post_id
          ? { ...fi, _isSaved: !fi._isSaved }
          : fi
      ));
    } catch { /* ignore */ }
  };

  const handleSubscribe = async (tier) => {
    if (!user) { navigate('/auth'); return; }
    try {
      const res = await fetch(`${API_URL}/api/venues/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin, tier }),
      });
      if (res.ok) { const data = await res.json(); if (data.url) window.location.href = data.url; }
    } catch { /* ignore */ }
  };

  const handlePromoteCTA = () => {
    if (!user) { navigate('/auth'); return; }
    if (subStatus?.can_post) navigate('/create-post');
    else setShowSubModal(true);
  };

  const canPost = subStatus?.can_post;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="venues-page">

      {/* ── Hero – FoodQuest sub-style ── */}
      <div className="relative" style={{ minHeight: '260px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1400&q=80')" }} />
        <div className="relative h-full flex flex-col justify-end max-w-5xl mx-auto px-4 sm:px-6" style={{ minHeight: '260px' }}>
          {/* Text area with dark glass background */}
          <div className="w-full md:w-fit bg-black/45 backdrop-blur-md rounded-2xl p-4 md:p-5 mb-4">
            <div className="flex items-start gap-3 md:gap-5">
              <div className="w-[3px] rounded-full bg-[#FF6B35] self-stretch min-h-[50px] md:min-h-[60px] flex-shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-tight" data-testid="venues-hero-title">
                  Food Places <span className="text-[#F4C430]">Near You</span>
                </h1>
                <p className="text-white/80 text-xs md:text-sm">
                  Discover local spots, deals, events — and share your own food
                </p>
                {city && (
                  <p className="text-white/50 text-xs flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />{city}
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Promote CTA – no background */}
          <div className="mb-6">
            <button onClick={handlePromoteCTA} className="h-9 md:h-10 px-4 md:px-6 bg-gradient-to-b from-[#F5C842] via-[#FF9248] to-[#FF7340] text-white text-xs md:text-sm font-bold rounded-2xl flex items-center gap-1.5 hover:opacity-95 active:scale-95 transition-all shadow-lg whitespace-nowrap" data-testid="promote-cta-btn">
              {canPost ? <><Plus className="w-3.5 h-3.5" /> Post Your Food</> : <><Crown className="w-3.5 h-3.5" /> Promote Your Food</>}
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB BAR — sticky, clean, Uber Eats pill style
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide gap-1 py-2.5">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 ${
                    active
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'bg-transparent text-gray-500 hover:bg-gray-100'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          CONTENT GRID — feed + sidebar
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        <div className="flex gap-8">

          {/* ── Feed Column ── */}
          <main className="flex-1 min-w-0 max-w-2xl mx-auto lg:mx-0 space-y-5">
            {loading ? (
              <FeedSkeleton />
            ) : feedItems.length === 0 ? (
              /* Empty state */
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Sparkles className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">No posts yet</h3>
                <p className="text-sm text-gray-500 mb-8 max-w-xs mx-auto">Be the first to share food in your area</p>
                <button
                  onClick={handlePromoteCTA}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold text-sm rounded-full hover:bg-gray-800 active:scale-[0.97] transition-all"
                >
                  <Plus className="w-4 h-4" /> Create FoodCard
                </button>
              </div>
            ) : (
              <>
                {feedItems.map((item, idx) => (
                  <FeedCard key={item.post_id || item.content_id || idx} item={item} onLike={handleLike} onSave={handleSave} />
                ))}
                {hasMore && (
                  <button
                    onClick={loadMore}
                    className="w-full py-3.5 text-sm text-gray-500 font-medium hover:text-gray-900 hover:bg-white rounded-2xl transition-colors"
                    data-testid="load-more-btn"
                  >
                    Load more
                  </button>
                )}
                <div ref={feedEndRef} />
              </>
            )}
          </main>

          {/* ── Sidebar (desktop) ── */}
          <aside className="hidden lg:flex flex-col w-72 xl:w-80 flex-shrink-0 gap-5">

            {/* Trending */}
            <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
                <Flame className="w-4 h-4 text-[#FF6B35]" /> Trending
              </h3>
              {trendingTags.length > 0 ? (
                <div className="space-y-3">
                  {trendingTags.slice(0, 6).map(t => (
                    <div key={t.tag} className="flex items-center justify-between group cursor-pointer">
                      <span className="text-sm text-[#FF6B35] font-medium group-hover:underline">#{t.tag}</span>
                      <span className="text-[11px] text-gray-400">{t.count} posts</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No trending tags yet</p>
              )}
            </div>

            {/* Vibe Chips */}
            <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Quick Vibes</h3>
              <div className="flex flex-wrap gap-2">
                {VIBE_CHIPS.map(vibe => (
                  <button
                    key={vibe}
                    className="px-3.5 py-1.5 bg-gray-50 hover:bg-gray-900 hover:text-white text-gray-600 text-xs font-medium rounded-full transition-all duration-200"
                  >
                    {vibe}
                  </button>
                ))}
              </div>
            </div>

            {/* Top Members */}
            {topMembers.length > 0 && (
              <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-[#F4C430]" /> Top Members
                </h3>
                <div className="space-y-3">
                  {topMembers.map((m, i) => (
                    <div key={m.user_id || i} className="flex items-center gap-3 group cursor-pointer">
                      <img
                        src={m.picture || `https://i.pravatar.cc/150?u=${m.user_id}`}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-[#FF6B35]/30 transition-all"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#FF6B35] transition-colors">
                          {m.name || 'Foodie'}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {m.is_business ? 'Business' : m.tier === 'pro' ? 'Pro Member' : 'Member'}
                        </p>
                      </div>
                      {m.is_business && <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription nudge */}
            {user && !subStatus?.can_post && (
              <div className="bg-gray-900 rounded-[20px] p-5 text-white overflow-hidden relative">
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#FF6B35] rounded-full opacity-20 blur-2xl" />
                <Crown className="w-7 h-7 text-[#F4C430] mb-3" />
                <h3 className="font-bold text-base mb-1">Join Premium</h3>
                <p className="text-xs text-white/60 mb-5 leading-relaxed">Post & promote your food to the community · From $10/mo</p>
                <button
                  onClick={() => setShowSubModal(true)}
                  className="w-full py-3 bg-white text-gray-900 font-bold text-sm rounded-full hover:bg-gray-100 active:scale-[0.98] transition-all"
                  data-testid="sidebar-subscribe-btn"
                >
                  Get Started
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MOBILE BOTTOM BANNER — subscription nudge
          ══════════════════════════════════════════════════════════════════════ */}
      {user && !subStatus?.can_post && (
        <div className="lg:hidden fixed bottom-16 left-0 right-0 z-20 px-4 pb-2">
          <button
            onClick={() => setShowSubModal(true)}
            className="w-full bg-gray-900 text-white rounded-2xl px-5 py-3.5 flex items-center justify-between shadow-xl active:scale-[0.98] transition-transform"
            data-testid="mobile-subscribe-banner"
          >
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-[#F4C430]" />
              <span className="text-sm font-semibold">Join Premium — From $10/mo</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          FAB — floating create button
          ══════════════════════════════════════════════════════════════════════ */}
      <button
        onClick={handlePromoteCTA}
        className="fixed bottom-24 lg:bottom-8 right-5 lg:right-8 z-20 w-14 h-14 bg-gray-900 text-white rounded-full shadow-xl flex items-center justify-center hover:bg-gray-800 hover:scale-105 active:scale-95 transition-all duration-200"
        data-testid="fab-create-btn"
      >
        <Plus className="w-6 h-6" strokeWidth={2.5} />
      </button>

      {/* ══════════════════════════════════════════════════════════════════════
          SUBSCRIPTION MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      {showSubModal && (
        <SubscriptionModal
          onClose={() => setShowSubModal(false)}
          tiers={subStatus?.tiers || {
            basic: { price: 10, label: 'Basic', features: ['Post FoodCards', 'Basic visibility', 'Premium badge'] },
            pro:   { price: 20, label: 'Pro',   features: ['Priority placement', 'Analytics', 'Unlimited posts', 'Premium offers'] },
          }}
          onSubscribe={handleSubscribe}
        />
      )}
    </div>
  );
}
